import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from "@/shared/ui/appHeader/mobileBottomNav";

export const CHROME_OFFSET_VAR = "--chrome-offset";
/** Unitless copy of the offset so opacity can divide without mixing lengths. */
export const CHROME_OFFSET_N_VAR = "--chrome-offset-n";
/** Nav plus every registered bottom piece (the grid pagination bar). */
export const CHROME_BOTTOM_VAR = "--chrome-bottom";
export const CHROME_BOTTOM_N_VAR = "--chrome-bottom-n";
/** `none` once the hide offset has covered the bottom stack, so a faded FAB cannot take taps. */
export const CHROME_FAB_POINTER_VAR = "--chrome-fab-pointer";

/** In-flow top chrome starts below the fixed app bar. */
export const APP_HEADER_HEIGHT_PX = 64;

export type ChromeEdge = "top" | "bottom";

/**
 * Pixels of a piece hidden once `offset` has already consumed `start` px on the
 * same edge. The top edge starts at the header; the bottom edge starts at the nav.
 */
export function pieceHide(offset: number, start: number, height: number): number {
  if (height <= 0) {
    return 0;
  }
  const raw = offset - start;
  if (raw <= 0) {
    return 0;
  }
  if (raw >= height) {
    return height;
  }
  return raw;
}

export function clampChromeOffset(offset: number, max: number): number {
  if (max <= 0 || offset <= 0) {
    return 0;
  }
  if (offset >= max) {
    return max;
  }
  return offset;
}

/** `min(offset, height)` for the header and the bottom nav, which sit on the screen edge. */
export function chromeLeadingHide(heightPx: number): string {
  return `min(var(${CHROME_OFFSET_VAR}, 0px), ${heightPx}px)`;
}

/** Height still on screen for an edge piece of `heightPx`. */
export function chromeRemaining(heightPx: number): string {
  return `max(0px, ${heightPx}px - var(${CHROME_OFFSET_VAR}, 0px))`;
}

/** Full bottom stack: the nav, then the grid pagination once it has registered. */
export function chromeBottomStack(): string {
  return `var(${CHROME_BOTTOM_VAR}, ${MOBILE_BOTTOM_NAV_HEIGHT_PX}px)`;
}

/** Bottom stack still on screen. Zero once the nav and pagination have hidden. */
export function chromeBottomRemaining(): string {
  return `max(0px, ${chromeBottomStack()} - var(${CHROME_OFFSET_VAR}, 0px))`;
}

/**
 * Assistant button bottom. One standard gap above the table footer while that
 * footer is registered, and the usual inset above the nav when it is not.
 * The gap holds as those bars hide, so the button does not cross the footer.
 */
export function chromeFabBottom(edgePx: number): string {
  return `calc(${chromeBottomRemaining()} + env(safe-area-inset-bottom, 0px) + ${edgePx}px)`;
}

/**
 * Opacity for the assistant button. The fade covers the whole bottom stack
 * and returns to 1 on the way back.
 */
export function chromeFabOpacity(): string {
  const stack = `max(var(${CHROME_BOTTOM_N_VAR}, ${MOBILE_BOTTOM_NAV_HEIGHT_PX}), 1)`;
  return `calc(1 - min(var(${CHROME_OFFSET_N_VAR}, 0), ${stack}) / ${stack})`;
}

/**
 * Opacity for the header or the bottom nav. It reaches 0 at the same offset
 * that finishes that bar's slide, in either direction.
 */
export function chromeLeadingOpacity(heightPx: number): string {
  const span = Math.max(heightPx, 1);
  return `calc(1 - min(var(${CHROME_OFFSET_N_VAR}, 0), ${span}) / ${span})`;
}

/** Hide for a registered piece. `--chrome-before` is the px already consumed on that edge. */
export function chromePieceHide(): string {
  return `clamp(0px, var(${CHROME_OFFSET_VAR}, 0px) - var(--chrome-before, 0px), var(--chrome-piece-height, 0px))`;
}

/**
 * Opacity for a registered piece. The fade covers the same pixels as
 * `chromePieceHide`, so the block is clear when the slide finishes.
 */
export function chromePieceOpacity(): string {
  const span = "max(var(--chrome-piece-height-n, 1), 1)";
  const hidden = `min(max(var(${CHROME_OFFSET_N_VAR}, 0) - var(--chrome-before-n, 0), 0), ${span})`;
  return `calc(1 - ${hidden} / ${span})`;
}
